'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { useEstimator } from '../../context/EstimatorContext';
import useSavePipeSpecs from '../../hooks/useSavePipeSpecs';
import useFetchCoatingPricing from '../../hooks/useFetchCoatingPricing';
import { ClientSideRowModelModule, ModuleRegistry, PinnedRowModule } from 'ag-grid-community';
ModuleRegistry.registerModules([PinnedRowModule, ClientSideRowModelModule]);

const pipeOrder = [
  'Class 1',
  'Class 2',
  'Class 3',
  'Class 4',
  'Bore Pipe Class 1',
  'Bore Pipe Class 2',
  'Bore Pipe Class 3',
  'Direct Pipe',
  'HDD',
];

const PipeSpecsGrid = () => {
  const { formData, setFormData } = useEstimator();
  const { coatingPricing } = useFetchCoatingPricing();
  const savePipeSpecs = useSavePipeSpecs();
  const [saved, setSaved] = useState(false);

  const [loading, setLoading] = useState(false);

  const formatNegatives = (value, decimals = 3) => {
    if (value === '' || value === null || isNaN(value)) return '';
    return value < 0 ? `(${Math.abs(value).toFixed(decimals)})` : value.toFixed(decimals);
  };

  const [pipeSpecsData, setPipeSpecsData] = useState(() => {
    const initialSpecs =
      formData.pipeSpecs && formData.pipeSpecs.length > 0
        ? formData.pipeSpecs
        : [
            { lineClass: 'Class 1', baseQty: 0, classLocation: 0.72 },
            { lineClass: 'Class 2', baseQty: formData.quantities?.[23]?.quantity || 0, classLocation: 0.6 },
            { lineClass: 'Class 3', baseQty: formData.quantities?.[24]?.quantity || 0, classLocation: 0.5 },
            { lineClass: 'Class 4', baseQty: formData.quantities?.[25]?.quantity || 0, classLocation: 0.4 },
            { lineClass: 'Bore Pipe Class 1', baseQty: formData.quantities?.[13]?.quantity || 0, classLocation: 0.6 },
            { lineClass: 'Bore Pipe Class 2', baseQty: formData.quantities?.[15]?.quantity || 0, classLocation: 0.5 },
            { lineClass: 'Bore Pipe Class 3', baseQty: formData.quantities?.[17]?.quantity || 0, classLocation: 0.4 },
            { lineClass: 'Direct Pipe', baseQty: formData.quantities?.[20]?.quantity || 0, classLocation: 0.5 },
            { lineClass: 'HDD', baseQty: formData.quantities?.[19]?.quantity || 0, classLocation: 0.5 },
          ];

    const ordered = pipeOrder
      .map((lineClass) => initialSpecs.find((row) => row.lineClass === lineClass))
      .filter(Boolean);
    setFormData((prev) => ({ ...prev, pipeSpecs: ordered })); // also set context

    return ordered;
  });

  useEffect(() => {
    const initialSpecs =
      formData.pipeSpecs && formData.pipeSpecs.length > 0
        ? formData.pipeSpecs
        : [
            { lineClass: 'Class 1', baseQty: 0, classLocation: 0.72 },
            { lineClass: 'Class 2', baseQty: formData.quantities?.[23]?.quantity || 0, classLocation: 0.6 },
            { lineClass: 'Class 3', baseQty: formData.quantities?.[24]?.quantity || 0, classLocation: 0.5 },
            { lineClass: 'Class 4', baseQty: formData.quantities?.[25]?.quantity || 0, classLocation: 0.4 },
            { lineClass: 'Bore Pipe Class 1', baseQty: formData.quantities?.[13]?.quantity || 0, classLocation: 0.6 },
            { lineClass: 'Bore Pipe Class 2', baseQty: formData.quantities?.[15]?.quantity || 0, classLocation: 0.5 },
            { lineClass: 'Bore Pipe Class 3', baseQty: formData.quantities?.[17]?.quantity || 0, classLocation: 0.4 },
            { lineClass: 'Direct Pipe', baseQty: formData.quantities?.[20]?.quantity || 0, classLocation: 0.5 },
            { lineClass: 'HDD', baseQty: formData.quantities?.[19]?.quantity || 0, classLocation: 0.5 },
          ];

    const ordered = pipeOrder
      .map((lineClass) => initialSpecs.find((row) => row.lineClass === lineClass))
      .filter(Boolean);

    setFormData((prev) => ({ ...prev, pipeSpecs: ordered }));
  }, []);

  const tensileStrengthMap = {
    'X-42': 42000,
    'X-46': 46000,
    'X-52': 52000,
    'X-56': 56000,
    'X-60': 60000,
    'X-65': 65000,
    'X-70': 70000,
    'X-80': 80000,
    'X-90': 90000,
  };

  useEffect(() => {
    const totalOtherClasses = [
      'Class 2',
      'Class 3',
      'Class 4',
      'Bore Pipe Class 1',
      'Bore Pipe Class 2',
      'Bore Pipe Class 3',
      'Direct Pipe',
      'HDD',
    ].reduce((sum, cls) => {
      const match = pipeSpecsData.find((row) => row.lineClass === cls);
      return sum + (parseFloat(match?.baseQty) || 0);
    }, 0);

    const class1BaseQty = Math.max((formData.quantities?.[5]?.quantity || 0) - totalOtherClasses, 0);

    const updated = pipeSpecsData.map((row) => {
      const baseQty = row.lineClass === 'Class 1' ? class1BaseQty : parseFloat(row.baseQty) || 0;

      const classLocation = parseFloat(row.classLocation) || 0;
      const tensileStrength = parseFloat(row.tensileStrength) || 0;
      const maop = parseFloat(formData.quantities?.[8]?.quantity) || 0;
      const size = parseFloat(formData.quantities?.[0]?.quantity) || 0;
      const percentOverBuy = parseFloat(row.percentOverBuy) || 0;
      const wtOverride = parseFloat(row.wtOverride);

      // Default to existing orderQty unless it needs to be calculated
      const orderQty =
        row.orderQty && !isNaN(row.orderQty) ? row.orderQty : Math.round(baseQty * (1 + percentOverBuy / 100));

      const wallThickness =
        row.wallThickness && !isNaN(row.wallThickness)
          ? row.wallThickness
          : baseQty && tensileStrength && classLocation
            ? Math.ceil(((maop * size) / (2 * tensileStrength * classLocation)) * 1000) / 1000
            : null;

      const activeWall = isNaN(wtOverride) ? wallThickness : wtOverride;

      const tons =
        row.tons && !isNaN(row.tons)
          ? row.tons
          : baseQty && activeWall && !isNaN(activeWall)
            ? Math.round(
                ((10.69 * (size - activeWall) * activeWall * ((1 + percentOverBuy / 100) * orderQty)) / 2000) * 1000
              ) / 1000
            : 0;

      const lbsPerFt =
        row.lbsPerFt && !isNaN(row.lbsPerFt)
          ? row.lbsPerFt
          : baseQty && size
            ? Math.round(
                (isNaN(wtOverride) ? size * wallThickness - wallThickness ** 2 : size * wtOverride - wtOverride ** 2) *
                  10.69 *
                  1000
              ) / 1000
            : 0;

      let coatingCostPerFt = row.coatingCostPerFt || 0;
      let coatingPrice = row.coatingPrice || 0;

      if ((row.internalCoating === 'Yes' || row.internalCoating === true) && coatingPricing.length > 0 && size) {
        const matched = coatingPricing.find((item) => Number(item.pipeOD) === Number(size));
        console.log(matched);

        const isBorePipe =
          row.lineClass?.toLowerCase().includes('bore') || ['HDD', 'Direct Pipe'].includes(row.lineClass);

        if (matched) {
          if (isBorePipe && matched.aroPricePerFoot != null) {
            coatingCostPerFt = parseFloat(matched.aroPricePerFoot);
            coatingPrice = coatingCostPerFt * orderQty;
          } else if (!isBorePipe && matched.fbePricePerFoot != null) {
            coatingCostPerFt = parseFloat(matched.fbePricePerFoot);
            coatingPrice = coatingCostPerFt * orderQty;
          }
        }
      }

      return {
        ...row,
        baseQty,
        orderQty,
        wallThickness,
        tons,
        lbsPerFt,
        coatingCostPerFt,
        coatingPrice,
      };
    });

    const sorted = pipeOrder.map(
      (lineClass) =>
        updated.find((row) => row.lineClass === lineClass) || {
          lineClass,
          baseQty: 0,
          classLocation: 0,
        }
    );

    setPipeSpecsData(
      pipeOrder.map((lineClass) => updated.find((row) => row.lineClass === lineClass)).filter(Boolean) // remove any undefined rows
    );
    const orderedUpdated = pipeOrder
      .map((lineClass) => updated.find((row) => row.lineClass === lineClass))
      .filter(Boolean);

    setFormData((prev) => ({ ...prev, pipeSpecs: orderedUpdated }));
  }, [formData.quantities, coatingPricing]);

  const handleGridEdit = (event) => {
    const updated = [...pipeSpecsData];
    const row = { ...event.data };

    const baseQty = parseFloat(row.baseQty) || 0;
    const percentOverBuy = parseFloat(row.percentOverBuy) || 0;
    const maop = parseFloat(formData.quantities?.[8]?.quantity) || 0;
    const size = parseFloat(formData.quantities?.[0]?.quantity) || 0;

    const classLocation = parseFloat(row.classLocation) || 0;
    const wtOverride = parseFloat(row.wtOverride);
    const orderQty = parseFloat(row.orderQty) || 0;

    // Recalculate Order Quantity for Class 2–4 and Bore Pipe Classes
    if (
      [
        'Class 1',
        'Class 2',
        'Class 3',
        'Class 4',
        'Bore Pipe Class 1',
        'Bore Pipe Class 2',
        'Bore Pipe Class 3',
        'Direct Pipe',
        'HDD',
      ].includes(row.lineClass)
    ) {
      row.orderQty = Math.round(baseQty * (1 + percentOverBuy / 100));
    }

    // Set tensile strength based on grade
    if (row.grade && tensileStrengthMap[row.grade]) {
      row.tensileStrength = tensileStrengthMap[row.grade];
    }
    const updatedTensileStrength = parseFloat(row.tensileStrength) || 0;

    // Wall Thickness calculation using updated tensile strength
    if (baseQty === 0) {
      row.wallThickness = '';
    } else {
      try {
        const denominator = 2 * updatedTensileStrength * classLocation;
        if (denominator === 0) {
          row.wallThickness = 0;
        } else {
          const rawWall = (maop * size) / denominator;
          const roundedUpWall = Math.ceil(rawWall * 1000) / 1000;
          row.wallThickness = roundedUpWall;
        }
      } catch {
        row.wallThickness = 0;
      }
    }

    // Tons Calculation
    if (baseQty === 0) {
      row.tons = 0;
    } else {
      const activeWall = isNaN(wtOverride) ? row.wallThickness : wtOverride;

      // If wall thickness is 0, empty, or NaN, result is 0
      if (!activeWall || isNaN(activeWall)) {
        row.tons = 0;
      } else {
        try {
          const tons = (10.69 * (size - activeWall) * activeWall * ((1 + percentOverBuy / 100) * orderQty)) / 2000;
          row.tons = Math.round(tons);
        } catch {
          row.tons = 0;
        }
      }
    }

    // lbs/ft Calculation
    if (baseQty === 0) {
      row.lbsPerFt = 0;
    } else {
      try {
        const lbsPerFt = isNaN(wtOverride)
          ? (size * row.wallThickness - row.wallThickness ** 2) * 10.69
          : (size * wtOverride - wtOverride ** 2) * 10.69;

        row.lbsPerFt = Math.round(lbsPerFt * 100) / 100;
      } catch {
        row.lbsPerFt = 0;
      }
    }

    if (row.internalCoating === 'Yes' && coatingPricing.length > 0 && size) {
      const matched = coatingPricing.find((item) => Number(item.pipeOD) === Number(size));
      const isBorePipe =
        row.lineClass?.toLowerCase().includes('bore') || ['HDD', 'Direct Pipe'].includes(row.lineClass);

      if (matched) {
        const pricePerFoot = isBorePipe ? matched.aroPricePerFoot : matched.fbePricePerFoot;
        row.coatingCostPerFt = pricePerFoot ? parseFloat(pricePerFoot) : 0;
        row.coatingPrice = row.coatingCostPerFt * orderQty;
      } else {
        row.coatingCostPerFt = 0;
        row.coatingPrice = 0;
      }
    } else {
      row.coatingCostPerFt = 0;
      row.coatingPrice = 0;
    }

    updated[event.rowIndex] = row;
    const ordered = pipeOrder.map((lineClass) => updated.find((row) => row.lineClass === lineClass)).filter(Boolean);

    setPipeSpecsData(ordered);
    setFormData((prev) => ({ ...prev, pipeSpecs: ordered }));
  };

  const totalOrderQty = pipeSpecsData.reduce((sum, row) => {
    const val = parseFloat(row.orderQty);
    return sum + (isNaN(val) ? 0 : val);
  }, 0);

  const totalBaseQty = pipeSpecsData.reduce((sum, row) => {
    const val = parseFloat(row.baseQty);
    return sum + (isNaN(val) ? 0 : val);
  }, 0);

  const totalCoatingPrice = pipeSpecsData.reduce((sum, row) => {
    const val = parseFloat(row.coatingPrice);
    return sum + (isNaN(val) ? 0 : val);
  }, 0);

  const totalTons = pipeSpecsData.reduce((sum, row) => {
    const val = parseFloat(row.tons);
    return sum + (isNaN(val) ? 0 : val);
  }, 0);

  const columnDefs = useMemo(
    () => [
      {
        headerName: 'Line Class',
        field: 'lineClass',
        pinned: 'left',
        width: 160,
        cellStyle: { backgroundColor: '#f0f0f0', color: '#999' },
      },
      {
        headerName: 'Base Quantity (ft)',
        field: 'baseQty',
        width: 180,
        valueFormatter: ({ value }) => {
          if (value !== '' && !isNaN(Number(value))) {
            return Number(value).toLocaleString();
          }
          return value;
        },
        cellStyle: (params) => {
          return params.data?.lineClass === 'Total'
            ? { fontWeight: 'bold', backgroundColor: '#eee' }
            : { backgroundColor: '#f0f0f0', color: '#999' };
        },
      },
      { headerName: '% Over Buy', field: 'percentOverBuy', editable: true, width: 180 },
      {
        headerName: 'Order Quantity (ft)',
        field: 'orderQty',
        cellStyle: (params) =>
          params.node.rowPinned
            ? { backgroundColor: '#e0e0e0', fontWeight: 'bold', color: '#000' }
            : { backgroundColor: '#f0f0f0', color: '#999' },
        width: 180,
        valueFormatter: ({ value }) => {
          if (value !== '' && !isNaN(Number(value))) {
            return Number(value).toLocaleString();
          }
          return value;
        },
      },
      {
        headerName: 'Grade',
        field: 'grade',
        editable: true,
        width: 180,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: Object.keys(tensileStrengthMap) },
      },
      {
        headerName: 'MAOP',
        field: 'maop',
        valueGetter: (params) => (params.node?.rowPinned === 'bottom' ? '' : formData.quantities?.[8]?.quantity || 0),
        cellStyle: { backgroundColor: '#f0f0f0', color: '#999' },
        width: 180,
        valueFormatter: ({ value }) => {
          if (value !== '' && !isNaN(Number(value))) {
            return Number(value).toLocaleString();
          }
          return value;
        },
      },

      { headerName: 'WT Override', field: 'wtOverride', editable: true, width: 180 },
      {
        headerName: 'Wall Thickness',
        field: 'wallThickness',
        cellStyle: { backgroundColor: '#f0f0f0', color: '#999' },
        width: 180,
        valueFormatter: ({ value }) => {
          if (value === '' || value === null || isNaN(value)) return '';
          return Number(value).toFixed(3);
        },
      },
      {
        headerName: 'Size',
        field: 'size',
        valueGetter: (params) => (params.node?.rowPinned === 'bottom' ? '' : formData.quantities?.[0]?.quantity || 0),
        cellStyle: { backgroundColor: '#f0f0f0', color: '#999' },
        width: 180,
      },

      {
        headerName: 'Tensile Strength',
        field: 'tensileStrength',
        editable: false,
        width: 180,
        cellStyle: { backgroundColor: '#f0f0f0', color: '#999' },
        valueFormatter: ({ value }) =>
          value === '' || value === null || isNaN(Number(value)) ? '' : Number(value).toLocaleString(),
      },
      {
        headerName: 'Class Location',
        field: 'classLocation',
        cellStyle: { backgroundColor: '#f0f0f0', color: '#999' },
        width: 180,
      },
      {
        headerName: 'Tons',
        field: 'tons',
        width: 180,
        valueFormatter: ({ value }) => {
          if (value !== '' && !isNaN(Number(value))) {
            return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          }
          return value;
        },
        cellStyle: (params) => {
          return params.data?.lineClass === 'Total'
            ? { fontWeight: 'bold', backgroundColor: '#eee' }
            : { backgroundColor: '#f0f0f0', color: '#999' };
        },
      },
      {
        headerName: 'lbs/ft',
        field: 'lbsPerFt',
        width: 180,
        cellStyle: { backgroundColor: '#f0f0f0', color: '#999' },
        valueFormatter: ({ value }) => formatNegatives(value),
      },
      {
        headerName: 'Internal Coating',
        field: 'internalCoating',
        editable: true,
        width: 180,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: ['Yes', 'No'] },
        valueSetter: (params) => {
          const oldValue = params.data.internalCoating;
          const newValue = params.newValue;

          // Only update if changed
          if (oldValue !== newValue) {
            params.data.internalCoating = newValue;
            return true;
          }

          return false; // prevents onCellValueChanged from firing if not changed
        },
        onCellValueChanged: handleGridEdit,
      },

      {
        headerName: 'Coating $/ft',
        field: 'coatingCostPerFt',
        width: 180,
        valueFormatter: ({ value }) => (value < 0 ? `(${Math.abs(value).toFixed(2)})` : value?.toFixed(2) || ''),
        cellStyle: { backgroundColor: '#f0f0f0', color: '#999' },
      },
      {
        headerName: 'Coating Price',
        field: 'coatingPrice',
        width: 180,
        valueFormatter: ({ value }) => {
          if (value !== '' && !isNaN(Number(value))) {
            return Number(value).toLocaleString();
          }
          return value;
        },
        cellStyle: (params) => {
          return params.data?.lineClass === 'Total'
            ? { fontWeight: 'bold', backgroundColor: '#eee' }
            : { backgroundColor: '#f0f0f0', color: '#999' };
        },
      },
    ],
    []
  );

  const pinnedBottomRowData = [
    {
      lineClass: 'Total',
      baseQty: totalBaseQty,
      orderQty: totalOrderQty,
      coatingPrice: totalCoatingPrice,
      tons: totalTons,
    },
  ];

  const handleSave = async () => {
    setLoading(true);
    try {
      await savePipeSpecs({
        estimateId: formData.estimateId,
        specs: pipeSpecsData,
      });

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save pipe specs:', error);
      alert('Failed to save. See console for details.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography
        variant="h6"
        mb={2}
        sx={{
          backgroundColor: '#0C1D3C',
          color: 'white',
          padding: '8px 16px',
          borderRadius: 1,
        }}
      >
        Pipe Specs
      </Typography>
      <Box sx={{ overflowY: 'auto', width: '100%' }}>
        <AgGridReact
          rowData={pipeSpecsData}
          columnDefs={columnDefs}
          onCellValueChanged={handleGridEdit}
          defaultColDef={{ resizable: true, cellStyle: { textAlign: 'left' } }}
          pinnedBottomRowData={pinnedBottomRowData}
          suppressMovableColumns
          domLayout="autoHeight"
        />
      </Box>
      <Box mt={2} textAlign="right">
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading}
          startIcon={loading && <CircularProgress size={18} color="inherit" />}
          sx={{
            backgroundColor: saved ? '#4CAF50' : '#4A90E2',
            '&:hover': {
              backgroundColor: saved ? '#43A047' : '#357ABD',
            },
          }}
        >
          {loading ? 'Saving...' : saved ? 'Saved!' : 'Save'}
        </Button>
      </Box>
    </Box>
  );
};

export default PipeSpecsGrid;
